import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Loader2, MessageSquare, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  converted: "bg-green-100 text-green-700",
  closed: "bg-gray-100 text-gray-500",
};

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEnquiries(); }, []);

  const fetchEnquiries = async () => {
    try {
      const res = await fetch(`${API}/admin/enquiries`, { credentials: "include" });
      if (res.ok) setEnquiries(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/admin/enquiries/${id}/status`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success("Status updated");
        fetchEnquiries();
      }
    } catch (e) { toast.error("Failed to update"); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-enquiries">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Demo Enquiries</h1>
          <p className="text-gray-500 text-sm mt-1">Manage enquiries from the landing page</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 text-green-500 animate-spin" /></div>
        ) : enquiries.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No enquiries yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {enquiries.map((enq) => (
              <div key={enq.enquiry_id} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`enquiry-${enq.enquiry_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{enq.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[enq.status] || STATUS_COLORS.new}`}>
                        {enq.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Mail size={13} /> {enq.email}</span>
                      <span className="flex items-center gap-1"><Phone size={13} /> {enq.country_code} {enq.phone}</span>
                      {enq.preferred_time && <span className="flex items-center gap-1"><Clock size={13} /> {enq.preferred_time}</span>}
                    </div>
                    {enq.area_of_interest && (
                      <p className="text-xs text-gray-600"><span className="font-semibold">Interest:</span> {enq.area_of_interest}</p>
                    )}
                    {enq.message && (
                      <p className="text-xs text-gray-600"><span className="font-semibold">Message:</span> {enq.message}</p>
                    )}
                    <p className="text-[10px] text-gray-500">{formatDate(enq.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {enq.status !== "contacted" && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => updateStatus(enq.enquiry_id, "contacted")} data-testid={`mark-contacted-${enq.enquiry_id}`}>
                        Contacted
                      </Button>
                    )}
                    {enq.status !== "converted" && (
                      <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(enq.enquiry_id, "converted")} data-testid={`mark-converted-${enq.enquiry_id}`}>
                        Converted
                      </Button>
                    )}
                    {enq.status !== "closed" && (
                      <Button size="sm" variant="outline" className="text-[10px] text-gray-500" onClick={() => updateStatus(enq.enquiry_id, "closed")} data-testid={`mark-closed-${enq.enquiry_id}`}>
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
